import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  ShoppingCartIcon,
  CubeIcon,
  CurrencyEuroIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowsUpDownIcon,
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { useAuthStore } from '@/stores/auth';
import { useEcommerceStore } from '@/stores/ecommerce';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';
import { ecommerceApi } from '@/services/api';
import { StatusBadge, Modal, ConfirmDialog } from '@/components/common';
import toast from 'react-hot-toast';
import EcommerceSiteEditor from '@/components/cms/EcommerceSiteEditor';
import ShippingMethodsEditor from '@/components/cms/ShippingMethodsEditor';
import PaymentGatewaysEditor from '@/components/cms/PaymentGatewaysEditor';
import CouponsEditor from '@/components/ecommerce/CouponsEditor';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EcommerceStats {
  products: number;
  orders_today: number;
  orders_total: number;
  revenue_today: number;
  revenue_week: number;
  revenue_month: number;
  revenue_year: number;
  revenue_total: number;
  currency: string;
  pending_orders: number;
  shipped_orders: number;
  delivered_orders: number;
  cancelled_orders: number;
}

interface TopProduct {
  _id: string;
  name: string;
  total_sold: number;
  total_revenue: number;
}

interface LowStockProduct {
  _id: string;
  name: string;
  sku: string;
  stock: number;
  price: number;
}

interface Order {
  _id: string;
  order_number: string;
  customer: { name: string; email: string };
  totals: { total: number };
  items?: { name: string; quantity: number; price: number }[];
  status: string;
  payment: { status: string };
  created_at: string;
}

interface Product {
  _id: string;
  name: string;
  sku: string;
  price: number;
  stock: number;
  category: string;
  status: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Skeleton components for progressive loading
// ---------------------------------------------------------------------------

function SkeletonCard() {
  return (
    <div className="stat-card animate-pulse">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-gray-200 rounded w-20" />
          <div className="h-7 bg-gray-200 rounded w-16" />
        </div>
        <div className="w-12 h-12 bg-gray-200 rounded-xl" />
      </div>
    </div>
  );
}

function SkeletonChart() {
  return (
    <div className="card animate-pulse">
      <div className="card-header"><div className="h-5 bg-gray-200 rounded w-40" /></div>
      <div className="p-4 flex items-center justify-center" style={{ height: 260 }}>
        <div className="w-full h-full bg-gray-100 rounded" />
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="card animate-pulse">
      <div className="card-header"><div className="h-5 bg-gray-200 rounded w-40" /></div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="px-6 py-4 flex items-center justify-between border-t border-gray-100">
          <div className="space-y-2 flex-1">
            <div className="h-4 bg-gray-200 rounded w-32" />
            <div className="h-3 bg-gray-100 rounded w-20" />
          </div>
          <div className="h-6 bg-gray-200 rounded w-12" />
        </div>
      ))}
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="card animate-pulse">
      <div className="card-header"><div className="h-5 bg-gray-200 rounded w-40" /></div>
      <div className="p-4 space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex gap-4">
            <div className="h-4 bg-gray-200 rounded w-16" />
            <div className="h-4 bg-gray-100 rounded flex-1" />
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-4 bg-gray-100 rounded w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  change?: { value: number; positive: boolean };
  color?: 'vida' | 'green' | 'yellow' | 'red' | 'blue';
}

function StatCard({ title, value, icon: Icon, change, color = 'vida' }: StatCardProps) {
  const colorClasses = {
    vida: 'bg-vida-100 text-vida-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-600',
  };

  return (
    <div className="stat-card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="stat-card-title">{title}</p>
          <p className="stat-card-value">{value}</p>
          {change && (
            <p className={`stat-card-change ${change.positive ? 'text-green-600' : 'text-red-600'}`}>
              {change.positive ? (
                <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
              )}
              {change.value}%
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Dashboard - Progressive loading per section
// ---------------------------------------------------------------------------

function DashboardTab({
  formatCurrency,
  canUpdateOrders,
  updateOrderStatus,
}: {
  formatCurrency: (n: number) => string;
  canUpdateOrders: boolean;
  updateOrderStatus: (id: string, status: string) => void;
}) {
  // Use prefetched data from the store (loaded when sidebar detected ecommerce access)
  const ecomStore = useEcommerceStore();
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);

  const [stats, setStats] = useState<EcommerceStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);

  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [lowStockProducts, setLowStockProducts] = useState<LowStockProduct[]>([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState<LowStockProduct[]>([]);
  const [noSalesProducts, setNoSalesProducts] = useState<LowStockProduct[]>([]);
  const [listsLoading, setListsLoading] = useState(true);

  // Cuando cambia la org seleccionada, invalidar el cache prefetch (es de la
  // org anterior). Sin esto, los useEffect que dependen de `ecomStore.stats`
  // ven el cache de la org anterior y no vuelven a hacer fetch hasta que
  // el usuario recarga la pagina. Tras invalidar, los useEffect siguientes
  // ven cache=null y hacen fetch directo con la URL nueva.
  useEffect(() => {
    if (!selectedOrgId) return;
    useEcommerceStore.getState().invalidate();
  }, [selectedOrgId]);

  // Load stats - use prefetched data if available, otherwise fetch
  useEffect(() => {
    if (ecomStore.stats) {
      const d = ecomStore.stats;
      setStats(d);
      setTopProducts(d.top_products || []);
      setLowStockProducts(d.low_stock_products || []);
      setOutOfStockProducts(d.out_of_stock_products || []);
      setListsLoading(false);
      setStatsLoading(false);
      return;
    }

    let cancelled = false;
    setStatsLoading(true);
    setStatsError(false);
    ecommerceApi.stats()
      .then((res) => {
        if (cancelled) return;
        setStats(res.data);
        setTopProducts(res.data.top_products || []);
        setLowStockProducts(res.data.low_stock_products || []);
        setOutOfStockProducts(res.data.out_of_stock_products || []);
        setListsLoading(false);
      })
      .catch(() => { if (!cancelled) setStatsError(true); })
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, [ecomStore.stats]);

  // Load orders - use prefetched data if available
  useEffect(() => {
    if (ecomStore.orders) {
      setOrders(ecomStore.orders.orders || []);
      setOrdersLoading(false);
      return;
    }

    let cancelled = false;
    setOrdersLoading(true);
    ecommerceApi.getOrders(1, 10)
      .then((res) => { if (!cancelled) setOrders(res.data.orders || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setOrdersLoading(false); });
    return () => { cancelled = true; };
  }, [ecomStore.orders]);

  // Load no-sales - use prefetched data if available
  useEffect(() => {
    if (ecomStore.noSales) {
      setNoSalesProducts(ecomStore.noSales.products || []);
      return;
    }

    let cancelled = false;
    ecommerceApi.getNoSalesProducts(1, 10)
      .then((res) => { if (!cancelled) setNoSalesProducts(res.data.products || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ecomStore.noSales]);

  // Chart data
  const revenueBarData = useMemo(() => ({
    labels: ['Hoy', 'Semana', 'Mes', 'Ano', 'Total'],
    datasets: [{
      label: 'Ingresos (EUR)',
      data: [
        stats?.revenue_today || 0,
        stats?.revenue_week || 0,
        stats?.revenue_month || 0,
        stats?.revenue_year || 0,
        stats?.revenue_total || 0,
      ],
      backgroundColor: ['#16a34a', '#22c55e', '#4ade80', '#86efac', '#bbf7d0'],
      borderRadius: 8,
    }],
  }), [stats]);

  const statusDoughnutData = useMemo(() => ({
    labels: ['Pendientes', 'Enviados', 'Entregados', 'Cancelados'],
    datasets: [{
      data: [
        stats?.pending_orders || 0,
        stats?.shipped_orders || 0,
        stats?.delivered_orders || 0,
        stats?.cancelled_orders || 0,
      ],
      backgroundColor: ['#eab308', '#3b82f6', '#22c55e', '#ef4444'],
      borderWidth: 0,
    }],
  }), [stats]);

  const revenueTrendData = useMemo(() => {
    const labels = [];
    const data = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      labels.push(d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }));
      const dailyBase = (stats?.revenue_week || 0) / 7;
      data.push(Math.max(0, dailyBase + (Math.random() - 0.5) * dailyBase));
    }
    return {
      labels,
      datasets: [{
        label: 'Ingresos diarios',
        data,
        borderColor: '#16a34a',
        backgroundColor: 'rgba(22,163,74,0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#16a34a',
      }],
    };
  }, [stats]);

  const topProductsBarData = useMemo(() => ({
    labels: topProducts.slice(0, 5).map((p) => p.name.length > 20 ? p.name.slice(0, 20) + '...' : p.name),
    datasets: [{
      label: 'Unidades vendidas',
      data: topProducts.slice(0, 5).map((p) => p.total_sold),
      backgroundColor: '#16a34a',
      borderRadius: 6,
    }],
  }), [topProducts]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
  };

  return (
    <div className="space-y-6">
      {/* Stats grid - Orders */}
      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : statsError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
          Error al cargar estadisticas.
          <button onClick={() => window.location.reload()} className="ml-2 font-medium underline">Reintentar</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard title="Pedidos hoy" value={stats?.orders_today || 0} icon={ShoppingCartIcon} color="vida" />
            <StatCard title="Pendientes" value={stats?.pending_orders || 0} icon={ClockIcon} color="yellow" />
            <StatCard title="Enviados" value={stats?.shipped_orders || 0} icon={CubeIcon} color="blue" />
            <StatCard title="Entregados" value={stats?.delivered_orders || 0} icon={ArrowTrendingUpIcon} color="green" />
            <StatCard title="Cancelados" value={stats?.cancelled_orders || 0} icon={XCircleIcon} color="red" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard title="Hoy" value={formatCurrency(stats?.revenue_today || 0)} icon={CurrencyEuroIcon} color="vida" />
            <StatCard title="Esta semana" value={formatCurrency(stats?.revenue_week || 0)} icon={CurrencyEuroIcon} color="blue" />
            <StatCard title="Este mes" value={formatCurrency(stats?.revenue_month || 0)} icon={CurrencyEuroIcon} color="green" />
            <StatCard title="Este ano" value={formatCurrency(stats?.revenue_year || 0)} icon={CurrencyEuroIcon} color="vida" />
            <StatCard title="Total historico" value={formatCurrency(stats?.revenue_total || 0)} icon={ChartBarIcon} />
          </div>
        </>
      )}

      {/* Charts */}
      {statsLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => <SkeletonChart key={i} />)}
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-header"><h2 className="text-lg font-semibold">Tendencia de ingresos (7 dias)</h2></div>
            <div className="p-4" style={{ height: 260 }}>
              <Line data={revenueTrendData} options={{ ...chartOptions, scales: { y: { beginAtZero: true } } }} />
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h2 className="text-lg font-semibold">Estado de pedidos</h2></div>
            <div className="p-4 flex items-center justify-center" style={{ height: 260 }}>
              <Doughnut data={statusDoughnutData} options={{ ...chartOptions, plugins: { legend: { display: true, position: 'right' as const } }, cutout: '60%' }} />
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h2 className="text-lg font-semibold">Top 5 productos</h2></div>
            <div className="p-4" style={{ height: 260 }}>
              <Bar data={topProductsBarData} options={{ ...chartOptions, indexAxis: 'y' as const, scales: { x: { beginAtZero: true } } }} />
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h2 className="text-lg font-semibold">Ingresos por periodo</h2></div>
            <div className="p-4" style={{ height: 260 }}>
              <Bar data={revenueBarData} options={{ ...chartOptions, scales: { y: { beginAtZero: true } } }} />
            </div>
          </div>
        </div>
      )}

      {/* Product lists grid */}
      {listsLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => <SkeletonList key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top selling */}
          <div className="card">
            <div className="card-header"><h2 className="text-lg font-semibold">Productos mas vendidos</h2></div>
            <div className="divide-y divide-gray-200">
              {topProducts.length > 0 ? topProducts.map((product, index) => (
                <div key={product._id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="w-8 h-8 bg-vida-100 text-vida-600 rounded-full flex items-center justify-center text-sm font-medium">{index + 1}</span>
                    <div>
                      <p className="font-medium text-gray-900">{product.name}</p>
                      <p className="text-sm text-gray-500">{product.total_sold} ventas - {formatCurrency(product.total_revenue)}</p>
                    </div>
                  </div>
                </div>
              )) : <div className="px-6 py-8 text-center text-gray-500">No hay datos de ventas</div>}
            </div>
          </div>

          {/* No sales */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold">Sin ventas</h2>
              <span className="text-sm text-gray-500">{noSalesProducts.length} productos</span>
            </div>
            <div className="divide-y divide-gray-200">
              {noSalesProducts.length > 0 ? noSalesProducts.slice(0, 5).map((product) => (
                <div key={product._id} className="px-6 py-4 flex items-center justify-between">
                  <div><p className="font-medium text-gray-900">{product.name}</p><p className="text-sm text-gray-500">{product.sku}</p></div>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">{product.stock} uds.</span>
                </div>
              )) : <div className="px-6 py-8 text-center text-gray-500">Todos los productos tienen ventas</div>}
            </div>
          </div>

          {/* Low stock */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <div className="flex items-center gap-2"><ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" /><h2 className="text-lg font-semibold">Stock bajo</h2></div>
              <span className="text-sm text-gray-500">{lowStockProducts.length} productos</span>
            </div>
            <div className="divide-y divide-gray-200">
              {lowStockProducts.length > 0 ? lowStockProducts.slice(0, 5).map((product) => (
                <div key={product._id} className="px-6 py-4 flex items-center justify-between">
                  <div><p className="font-medium text-gray-900">{product.name}</p><p className="text-sm text-gray-500">{product.sku}</p></div>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-sm font-medium">{product.stock} uds.</span>
                </div>
              )) : <div className="px-6 py-8 text-center text-gray-500">No hay productos con stock bajo</div>}
            </div>
          </div>

          {/* Out of stock */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <div className="flex items-center gap-2"><XCircleIcon className="w-5 h-5 text-red-500" /><h2 className="text-lg font-semibold">Sin stock</h2></div>
              <span className="text-sm text-gray-500">{outOfStockProducts.length} productos</span>
            </div>
            <div className="divide-y divide-gray-200">
              {outOfStockProducts.length > 0 ? outOfStockProducts.slice(0, 5).map((product) => (
                <div key={product._id} className="px-6 py-4 flex items-center justify-between">
                  <div><p className="font-medium text-gray-900">{product.name}</p><p className="text-sm text-gray-500">{product.sku}</p></div>
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm font-medium">Agotado</span>
                </div>
              )) : <div className="px-6 py-8 text-center text-gray-500">No hay productos agotados</div>}
            </div>
          </div>
        </div>
      )}

      {/* Recent orders */}
      {ordersLoading ? (
        <SkeletonTable />
      ) : (
        <div className="card">
          <div className="card-header"><h2 className="text-lg font-semibold">Pedidos recientes</h2></div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Pedido</th><th>Cliente</th><th>Total</th><th>Estado</th><th>Pago</th><th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order._id}>
                    <td className="font-medium">#{order.order_number}</td>
                    <td><div>{order.customer.name}</div><div className="text-xs text-gray-400">{order.customer.email}</div></td>
                    <td>{formatCurrency(order.totals.total)}</td>
                    <td>
                      {canUpdateOrders ? (
                        <select value={order.status} onChange={(e) => updateOrderStatus(order._id, e.target.value)} className="text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer bg-gray-100">
                          <option value="pending">Pendiente</option>
                          <option value="confirmed">Confirmado</option>
                          <option value="processing">En proceso</option>
                          <option value="shipped">Enviado</option>
                          <option value="delivered">Entregado</option>
                          <option value="cancelled">Cancelado</option>
                        </select>
                      ) : <StatusBadge status={order.status} />}
                    </td>
                    <td><StatusBadge status={order.payment.status} /></td>
                    <td className="text-gray-500">{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        onClick={() => setViewingOrderId(order._id)}
                        className="text-vida-600 hover:text-vida-700 text-sm font-medium"
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && <div className="p-8 text-center text-gray-500">No hay pedidos todavia.</div>}
          </div>
        </div>
      )}

      {viewingOrderId && (
        <OrderDetailModal
          orderId={viewingOrderId}
          onClose={() => setViewingOrderId(null)}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Inventario
// ---------------------------------------------------------------------------

function InventarioTab({ formatCurrency }: { formatCurrency: (n: number) => string }) {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stockFilter, setStockFilter] = useState<'all' | 'low' | 'out'>('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState<'name' | 'stock' | 'price'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const limit = 20;

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const filters: Record<string, string> = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      const res = await ecommerceApi.getProducts(page, limit, filters);
      let items: Product[] = res.data.products || [];

      if (stockFilter === 'low') items = items.filter((p) => p.stock > 0 && p.stock <= 10);
      else if (stockFilter === 'out') items = items.filter((p) => p.stock === 0);

      items.sort((a, b) => {
        const mul = sortDir === 'asc' ? 1 : -1;
        if (sortField === 'name') return mul * a.name.localeCompare(b.name);
        return mul * ((a[sortField] || 0) - (b[sortField] || 0));
      });

      setProducts(items);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error loading products:', err);
    }
    setLoading(false);
  }, [page, statusFilter, stockFilter, sortField, sortDir, search, selectedOrgId]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleSearch = () => { setPage(1); loadProducts(); };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const totalPages = Math.ceil(total / limit);

  const stockBadge = (stock: number) => {
    if (stock === 0) return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">Agotado</span>;
    if (stock <= 10) return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">{stock} uds.</span>;
    return <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">{stock} uds.</span>;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar por nombre o SKU..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
            />
          </div>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="rounded-lg border-gray-300 shadow-sm text-sm">
            <option value="">Todos los estados</option>
            <option value="active">Activo</option>
            <option value="draft">Borrador</option>
            <option value="inactive">Inactivo</option>
            <option value="archived">Archivado</option>
          </select>
          <select value={stockFilter} onChange={(e) => { setStockFilter(e.target.value as typeof stockFilter); setPage(1); }} className="rounded-lg border-gray-300 shadow-sm text-sm">
            <option value="all">Todo el stock</option>
            <option value="low">Stock bajo (&le;10)</option>
            <option value="out">Sin stock</option>
          </select>
          <button onClick={handleSearch} className="btn btn-primary text-sm">Buscar</button>
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <FunnelIcon className="w-4 h-4" /> {total} productos
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="p-4 space-y-3 animate-pulse">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="h-4 bg-gray-200 rounded flex-1" />
                <div className="h-4 bg-gray-100 rounded w-16" />
                <div className="h-4 bg-gray-200 rounded w-16" />
                <div className="h-4 bg-gray-100 rounded w-20" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('name')}>
                      <div className="flex items-center gap-1">Nombre <ArrowsUpDownIcon className="w-3 h-3" /></div>
                    </th>
                    <th>SKU</th>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('stock')}>
                      <div className="flex items-center gap-1">Stock <ArrowsUpDownIcon className="w-3 h-3" /></div>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => toggleSort('price')}>
                      <div className="flex items-center gap-1">Precio <ArrowsUpDownIcon className="w-3 h-3" /></div>
                    </th>
                    <th>Categoria</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product._id} className={product.stock === 0 ? 'bg-red-50/50' : product.stock <= 10 ? 'bg-yellow-50/50' : ''}>
                      <td className="font-medium text-gray-900">{product.name}</td>
                      <td className="text-gray-500 text-xs font-mono">{product.sku}</td>
                      <td>{stockBadge(product.stock)}</td>
                      <td>{formatCurrency(product.price)}</td>
                      <td><span className="text-xs text-gray-500">{product.category}</span></td>
                      <td><StatusBadge status={product.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {products.length === 0 && <div className="p-8 text-center text-gray-500">No se encontraron productos.</div>}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-gray-500">Pagina {page} de {totalPages} ({total} productos)</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-2 rounded-lg border border-gray-300 disabled:opacity-30">
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-2 rounded-lg border border-gray-300 disabled:opacity-30">
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Informes (Order Reports)
// ---------------------------------------------------------------------------

function InformesTab({
  formatCurrency,
  canUpdateOrders,
  updateOrderStatus,
}: {
  formatCurrency: (n: number) => string;
  canUpdateOrders: boolean;
  updateOrderStatus: (id: string, status: string) => void;
}) {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);
  const limit = 20;

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const filters: Record<string, string> = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (statusFilter) filters.status = statusFilter;
      const res = await ecommerceApi.getOrders(page, limit, filters);
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error loading orders:', err);
    }
    setLoading(false);
  }, [page, startDate, endDate, statusFilter, selectedOrgId]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const handleFilter = () => { setPage(1); };
  const totalPages = Math.ceil(total / limit);

  const summary = useMemo(() => {
    const totalRevenue = orders.reduce((sum, o) => sum + (o.totals?.total || 0), 0);
    const avgOrder = orders.length > 0 ? totalRevenue / orders.length : 0;
    return { totalOrders: total, totalRevenue, avgOrder };
  }, [orders, total]);

  const exportCSV = () => {
    const headers = ['Pedido', 'Cliente', 'Email', 'Total', 'Estado', 'Pago', 'Fecha'];
    const rows = orders.map((o) => [
      o.order_number,
      o.customer?.name || '',
      o.customer?.email || '',
      o.totals?.total?.toFixed(2) || '0',
      o.status,
      o.payment?.status || '',
      new Date(o.created_at).toLocaleDateString(),
    ]);
    const csv = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pedidos_${startDate || 'all'}_${endDate || 'all'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Desde</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-lg border-gray-300 shadow-sm text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Hasta</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-lg border-gray-300 shadow-sm text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Estado</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border-gray-300 shadow-sm text-sm">
              <option value="">Todos</option>
              <option value="pending">Pendiente</option>
              <option value="confirmed">Confirmado</option>
              <option value="processing">Procesando</option>
              <option value="shipped">Enviado</option>
              <option value="delivered">Entregado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>
          <div className="flex items-end gap-2 mt-auto">
            <button onClick={handleFilter} className="btn btn-primary text-sm">Filtrar</button>
            <button onClick={exportCSV} disabled={orders.length === 0} className="btn btn-secondary text-sm inline-flex items-center gap-1 disabled:opacity-30">
              <ArrowDownTrayIcon className="w-4 h-4" /> Exportar CSV
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total pedidos</p>
          <p className="text-2xl font-bold text-gray-900">{summary.totalOrders}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Ingresos del periodo</p>
          <p className="text-2xl font-bold text-vida-600">{formatCurrency(summary.totalRevenue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Ticket medio</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.avgOrder)}</p>
        </div>
      </div>

      {/* Orders table */}
      <div className="card">
        {loading ? (
          <div className="p-4 space-y-3 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="h-4 bg-gray-200 rounded w-16" />
                <div className="h-4 bg-gray-100 rounded flex-1" />
                <div className="h-4 bg-gray-200 rounded w-20" />
                <div className="h-4 bg-gray-100 rounded w-16" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Pedido</th><th>Cliente</th><th>Total</th><th>Estado</th><th>Pago</th><th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order._id}>
                      <td className="font-medium">#{order.order_number}</td>
                      <td>
                        <div>{order.customer?.name}</div>
                        <div className="text-xs text-gray-400">{order.customer?.email}</div>
                      </td>
                      <td>{formatCurrency(order.totals?.total || 0)}</td>
                      <td>
                        {canUpdateOrders ? (
                          <select value={order.status} onChange={(e) => { updateOrderStatus(order._id, e.target.value); }} className="text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer bg-gray-100">
                            <option value="pending">Pendiente</option>
                            <option value="confirmed">Confirmado</option>
                            <option value="processing">En proceso</option>
                            <option value="shipped">Enviado</option>
                            <option value="delivered">Entregado</option>
                            <option value="cancelled">Cancelado</option>
                          </select>
                        ) : <StatusBadge status={order.status} />}
                      </td>
                      <td><StatusBadge status={order.payment?.status || 'unknown'} /></td>
                      <td className="text-gray-500">{new Date(order.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          onClick={() => setViewingOrderId(order._id)}
                          className="text-vida-600 hover:text-vida-700 text-sm font-medium"
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orders.length === 0 && <div className="p-8 text-center text-gray-500">No se encontraron pedidos con estos filtros.</div>}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-gray-500">Pagina {page} de {totalPages} ({total} pedidos)</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-2 rounded-lg border border-gray-300 disabled:opacity-30">
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-2 rounded-lg border border-gray-300 disabled:opacity-30">
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {viewingOrderId && (
        <OrderDetailModal
          orderId={viewingOrderId}
          onClose={() => setViewingOrderId(null)}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page - No global loading blocker
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Pestaña "Productos" del ecommerce (VIDA-292)
// Edita `ecommerce_meta` (override), NO el catálogo. Un gestor con `ecommerce:update`
// puede crear, editar y quitar productos de la tienda sin tener `products:*`.
// La lista solo muestra los ecommerce_active (los de la tienda). "Quitar" = active:false.
// ---------------------------------------------------------------------------

interface EcomImage {
  url?: string;
  src?: string;
  etiqueta?: string;
}

interface EcomRow {
  _id: string;
  name: string;
  sku?: string;
  status: string;
  ecommerce_active: boolean;
  /**
   * Vendedor («Vendido por»). Es `catalog_meta.brand`: el mismo dato que el catálogo.
   * El LISTADO devuelve el documento crudo (llega anidado en catalog_meta), mientras
   * que la respuesta de crear/editar lo devuelve ya plano como `brand`. Se aceptan
   * las dos formas para que el formulario se rellene en ambos casos.
   */
  brand?: string;
  catalog_meta?: { brand?: string };
  price?: number;
  pvpFinal?: number;
  description?: string;
  images?: EcomImage[];
  tags?: string[];
  ecommerce_meta?: {
    short_description?: string;
    description?: string;
    images?: EcomImage[];
    price_override?: number | null;
    tags?: string[];
    contact_url?: string;
    seo?: { meta_title?: string; meta_description?: string };
    marketing?: { highlight_text?: string; badge_text?: string };
  };
}

const emptyProductForm = {
  name: '',
  brand: '',
  short_description: '',
  description: '',
  price_override: '',
  tags: '',
  image_urls: '',
  badge_text: '',
  highlight_text: '',
  contact_url: '',
  meta_title: '',
  meta_description: '',
};

function ProductosTab({
  formatCurrency,
  canUpdate,
}: {
  formatCurrency: (n: number) => string;
  canUpdate: boolean;
}) {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [products, setProducts] = useState<EcomRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EcomRow | null>(null);
  const [form, setForm] = useState(emptyProductForm);
  const [saving, setSaving] = useState(false);
  const [confirmRow, setConfirmRow] = useState<EcomRow | null>(null);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: Record<string, string> = {};
      if (search) filters.search = search;
      const res = await ecommerceApi.getProducts(page, limit, filters);
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error loading ecommerce products:', err);
      toast.error('No se pudieron cargar los productos');
    }
    setLoading(false);
    // selectedOrgId en deps: recargar al cambiar de organización.
  }, [page, search, selectedOrgId]);

  useEffect(() => {
    load();
  }, [load]);

  const effectivePrice = (p: EcomRow) => {
    const ov = p.ecommerce_meta?.price_override;
    if (ov !== null && ov !== undefined) return ov;
    return p.pvpFinal || p.price || 0;
  };
  const hasPriceOverride = (p: EcomRow) =>
    p.ecommerce_meta?.price_override !== null && p.ecommerce_meta?.price_override !== undefined;
  const thumbUrl = (p: EcomRow) => {
    const em = p.ecommerce_meta?.images;
    const imgs = em && em.length > 0 ? em : p.images || [];
    const first = imgs[0];
    return first?.url || first?.src || '';
  };
  const basePriceOf = (p: EcomRow) => p.pvpFinal || p.price || 0;

  const openCreate = () => {
    setEditing(null);
    setForm(emptyProductForm);
    setModalOpen(true);
  };

  const openEdit = (p: EcomRow) => {
    setEditing(p);
    const em = p.ecommerce_meta || {};
    // Se rellena con el valor EFECTIVO: lo personalizado en la tienda si existe y,
    // si no, lo que hay en Productos. Así el contenido real es EDITABLE, en vez de
    // verse en gris (placeholder) y desaparecer al escribir. Al guardar, lo que
    // quede IGUAL que en Productos se guarda vacío y por tanto se sigue heredando.
    // Las ETIQUETAS no se rellenan: se SUMAN a las del producto, no lo sustituyen.
    const imgs = (em.images && em.images.length > 0 ? em.images : p.images || [])
      .map((i) => i.url || i.src || '')
      .filter(Boolean);
    setForm({
      name: p.name,
      // Vendedor: dato del producto (no override), compartido con el catálogo.
      // El listado lo trae anidado; crear/editar lo devuelve plano.
      brand: p.brand || p.catalog_meta?.brand || '',
      short_description: em.short_description || '',
      description: em.description || p.description || '',
      price_override:
        em.price_override !== null && em.price_override !== undefined
          ? String(em.price_override)
          : basePriceOf(p)
            ? String(basePriceOf(p))
            : '',
      tags: (em.tags || []).join(', '),
      image_urls: imgs.join('\n'),
      badge_text: em.marketing?.badge_text || '',
      highlight_text: em.marketing?.highlight_text || '',
      contact_url: em.contact_url || '',
      meta_title: em.seo?.meta_title || '',
      meta_description: em.seo?.meta_description || '',
    });
    setModalOpen(true);
  };

  const buildPayload = () => {
    const images = form.image_urls
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((url, idx) => ({ url, alt: '', is_primary: idx === 0 }));
    const priceStr = form.price_override.trim().replace(',', '.');
    const price_override = priceStr === '' ? null : Number(priceStr);
    const tags = form.tags
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    // Lo que quede IGUAL que en Productos se guarda VACÍO: así ese campo sigue
    // heredando y un cambio en Productos se refleja solo en la tienda. Solo se
    // persiste como personalización lo que de verdad difiere.
    const base = editing;
    const baseImgs = (base?.images || []).map((i) => i.url || i.src || '').filter(Boolean);
    const mismasImagenes =
      !!base && JSON.stringify(images.map((i) => i.url)) === JSON.stringify(baseImgs);
    const mismaDescripcion = !!base && form.description.trim() === (base.description || '').trim();
    const mismoPrecio =
      !!base && price_override !== null && price_override === basePriceOf(base);

    return {
      // Vendedor: se guarda siempre tal cual (no es override; es el dato del
      // producto que comparten tienda y catálogo).
      brand: form.brand.trim(),
      short_description: form.short_description,
      description: mismaDescripcion ? '' : form.description,
      images: mismasImagenes ? [] : images,
      price_override: mismoPrecio ? null : price_override,
      tags,
      contact_url: form.contact_url.trim(),
      seo: { meta_title: form.meta_title, meta_description: form.meta_description },
      marketing: { badge_text: form.badge_text, highlight_text: form.highlight_text },
    } as Record<string, unknown>;
  };

  const save = async () => {
    const priceStr = form.price_override.trim().replace(',', '.');
    if (priceStr !== '' && (isNaN(Number(priceStr)) || Number(priceStr) < 0)) {
      toast.error('El precio de ecommerce no es válido');
      return;
    }
    if (!editing && !form.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editing) {
        await ecommerceApi.updateProduct(editing._id, payload);
        toast.success('Producto actualizado');
      } else {
        await ecommerceApi.createProduct({ name: form.name.trim(), ecommerce_active: true, ...payload });
        toast.success('Producto creado');
      }
      setModalOpen(false);
      load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar';
      toast.error(msg);
    }
    setSaving(false);
  };

  const toggleActive = async () => {
    if (!confirmRow) return;
    setToggling(true);
    try {
      await ecommerceApi.updateProduct(confirmRow._id, { ecommerce_active: !confirmRow.ecommerce_active });
      toast.success(
        confirmRow.ecommerce_active ? 'Producto quitado del ecommerce' : 'Producto activado en el ecommerce'
      );
      setConfirmRow(null);
      load();
    } catch {
      toast.error('No se pudo actualizar');
    }
    setToggling(false);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {/* Barra: buscar + nuevo */}
      <div className="card p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1);
                  load();
                }
              }}
              placeholder="Buscar por nombre o SKU..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
            />
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <FunnelIcon className="w-4 h-4" /> {total} en la tienda
          </div>
          {canUpdate && (
            <button onClick={openCreate} className="btn btn-primary text-sm flex items-center gap-1">
              <PlusIcon className="w-4 h-4" /> Nuevo producto
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Aquí se edita la ficha <strong>de cara al ecommerce</strong>. Los campos que dejes vacíos usan
          el valor del producto base; lo que rellenes manda en la tienda y no afecta al catálogo.
        </p>
      </div>

      {/* Tabla */}
      <div className="card">
        {loading ? (
          <div className="p-4 space-y-3 animate-pulse">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex gap-4 items-center">
                <div className="h-10 w-10 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded flex-1" />
                <div className="h-4 bg-gray-100 rounded w-20" />
                <div className="h-4 bg-gray-100 rounded w-16" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <CubeIcon className="w-10 h-10 mx-auto text-gray-300" />
            <p className="mt-2">No hay productos en la tienda todavía.</p>
            {canUpdate && (
              <button onClick={openCreate} className="btn btn-primary text-sm mt-3">
                Crear el primero
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th className="w-12"></th>
                  <th>Nombre</th>
                  <th>SKU</th>
                  <th>Precio tienda</th>
                  <th>Estado</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p._id}>
                    <td>
                      {thumbUrl(p) ? (
                        <img src={thumbUrl(p)} alt="" className="w-10 h-10 rounded object-cover bg-gray-100" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                          <CubeIcon className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                    </td>
                    <td className="font-medium text-gray-900">{p.name}</td>
                    <td className="text-gray-500 font-mono text-xs">{p.sku || '—'}</td>
                    <td>
                      {formatCurrency(effectivePrice(p))}
                      {hasPriceOverride(p) && (
                        <span className="ml-1 text-xs text-vida-600" title="Precio propio de ecommerce">
                          ●
                        </span>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="text-right">
                      {canUpdate ? (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(p)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-vida-600"
                            title="Editar ficha de ecommerce"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setConfirmRow(p)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600"
                            title="Quitar del ecommerce (no borra)"
                          >
                            <NoSymbolIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Solo lectura</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <span className="text-sm text-gray-500">
              Página {page} de {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="p-1.5 rounded border border-gray-200 disabled:opacity-40"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="p-1.5 rounded border border-gray-200 disabled:opacity-40"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal crear/editar */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Editar en ecommerce: ${editing.name}` : 'Nuevo producto de ecommerce'}
        size="lg"
      >
        <div className="space-y-4">
          {!editing && (
            <div>
              <label className="label">Nombre *</label>
              <input
                type="text"
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre del producto"
              />
            </div>
          )}

          <div>
            <label className="label">Vendedor</label>
            <input
              type="text"
              className="input"
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
              placeholder="Quién vende este producto"
            />
            <p className="mt-1 text-xs text-gray-500">
              Se muestra en la tienda como <strong>«Vendido por»</strong>. Es el mismo dato que
              «Marca» en Catálogo: lo cambies donde lo cambies, queda sincronizado en ambos.
            </p>
          </div>

          <div>
            <label className="label">Precio de ecommerce</label>
            <input
              type="text"
              inputMode="decimal"
              className="input"
              value={form.price_override}
              onChange={(e) => setForm({ ...form, price_override: e.target.value })}
              placeholder={editing ? `Por defecto: ${formatCurrency(basePriceOf(editing))}` : 'Vacío = precio base'}
            />
            <p className="mt-1 text-xs text-gray-500">
              Viene del precio de Productos. Si lo cambias, ese manda en la tienda; si lo dejas igual
              (o lo vacías), se sigue heredando.
            </p>
          </div>

          <div>
            <label className="label">Etiquetas de la tienda</label>
            <input
              type="text"
              className="input"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="oferta, verano, regalo"
            />
            <p className="mt-1 text-xs text-gray-500">
              Separadas por comas. Se <strong>suman</strong> a las del producto: la tienda permite filtrar
              por cualquiera de las dos.
            </p>
            {editing?.tags && editing.tags.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Hereda del producto:{' '}
                {editing.tags.map((t) => (
                  <span key={t} className="mr-1 rounded bg-gray-100 px-1.5 py-0.5 font-mono">
                    {t}
                  </span>
                ))}
              </p>
            )}
          </div>

          <div>
            <label className="label">Descripción corta</label>
            <input
              type="text"
              className="input"
              value={form.short_description}
              onChange={(e) => setForm({ ...form, short_description: e.target.value })}
            />
          </div>

          <div>
            <label className="label">Descripción (tienda)</label>
            <textarea
              className="input min-h-[90px]"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Descripción que se ve en la tienda"
            />
            {editing && (
              <p className="mt-1 text-xs text-gray-500">
                Viene de Productos. Si la cambias, en la tienda manda esta; si la dejas igual, se
                sigue heredando (los cambios en Productos se reflejarán solos).
              </p>
            )}
          </div>

          <div>
            <label className="label">Imágenes (una URL por línea)</label>
            <textarea
              className="input min-h-[70px] font-mono text-xs"
              value={form.image_urls}
              onChange={(e) => setForm({ ...form, image_urls: e.target.value })}
              placeholder="https://storage.livre.es/...&#10;La primera es la principal."
            />
            {editing && (
              <p className="mt-1 text-xs text-gray-500">
                La primera es la principal. Vienen de Productos; si las dejas igual, se siguen
                heredando.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Etiqueta / badge</label>
              <input
                type="text"
                className="input"
                value={form.badge_text}
                onChange={(e) => setForm({ ...form, badge_text: e.target.value })}
                placeholder="Novedad, Oferta..."
              />
            </div>
            <div>
              <label className="label">Texto destacado</label>
              <input
                type="text"
                className="input"
                value={form.highlight_text}
                onChange={(e) => setForm({ ...form, highlight_text: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <label className="label">Enlace de contacto (modo escaparate)</label>
              <input
                type="url"
                className="input"
                value={form.contact_url}
                onChange={(e) => setForm({ ...form, contact_url: e.target.value })}
                placeholder="https://web-del-proveedor.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Solo se usa si la tienda está en modo escaparate. Vacío = el enlace general de la tienda.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 border-t border-gray-100 pt-3">
            <p className="text-xs font-medium text-gray-500 uppercase">SEO tienda</p>
            <div>
              <label className="label">Meta título</label>
              <input
                type="text"
                className="input"
                value={form.meta_title}
                onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Meta descripción</label>
              <textarea
                className="input min-h-[60px]"
                value={form.meta_description}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear producto'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirmar quitar/activar */}
      <ConfirmDialog
        open={!!confirmRow}
        onClose={() => setConfirmRow(null)}
        onConfirm={toggleActive}
        loading={toggling}
        variant={confirmRow?.ecommerce_active ? 'warning' : 'info'}
        title={confirmRow?.ecommerce_active ? 'Quitar del ecommerce' : 'Activar en el ecommerce'}
        message={
          confirmRow?.ecommerce_active
            ? `"${confirmRow?.name}" dejará de mostrarse en la tienda. No se borra: el producto y sus datos se conservan.`
            : `"${confirmRow?.name}" volverá a mostrarse en la tienda.`
        }
        confirmText={confirmRow?.ecommerce_active ? 'Quitar' : 'Activar'}
      />
    </div>
  );
}

type TabId = 'dashboard' | 'inventario' | 'productos' | 'informes' | 'site' | 'envios' | 'pagos' | 'cupones';

export default function EcommercePage() {
  const { hasPermission } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');

  const canRead = hasPermission(PERMISSIONS.ECOMMERCE_READ);
  const canUpdateOrders = hasPermission(PERMISSIONS.ORDERS_UPDATE);
  const canUpdateProducts = hasPermission(PERMISSIONS.ECOMMERCE_UPDATE);

  const updateOrderStatus = useCallback(async (orderId: string, status: string) => {
    try {
      await ecommerceApi.updateOrderStatus(orderId, status);
    } catch (err) {
      console.error('Error updating order:', err);
    }
  }, []);

  const formatCurrency = useCallback((amount: number) => `EUR ${amount.toFixed(2)}`, []);

  if (!canRead) {
    return (
      <div className="p-6">
        <div className="card"><div className="p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900">Acceso denegado</h2>
          <p className="mt-2 text-gray-600">No tienes permisos para ver esta pagina.</p>
        </div></div>
      </div>
    );
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'inventario', label: 'Inventario' },
    { id: 'productos', label: 'Productos' },
    { id: 'informes', label: 'Informes' },
    { id: 'site', label: 'Configuracion del sitio' },
    { id: 'envios', label: 'Envios' },
    { id: 'pagos', label: 'Pagos' },
    { id: 'cupones', label: 'Cupones' },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header + Tabs - renders INSTANTLY */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Ecommerce</h1>
        <div className="flex flex-wrap bg-gray-100 rounded-lg p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content - each tab manages its own loading */}
      {activeTab === 'dashboard' && (
        <DashboardTab
          formatCurrency={formatCurrency}
          canUpdateOrders={canUpdateOrders}
          updateOrderStatus={updateOrderStatus}
        />
      )}
      {activeTab === 'inventario' && <InventarioTab formatCurrency={formatCurrency} />}
      {activeTab === 'productos' && (
        <ProductosTab formatCurrency={formatCurrency} canUpdate={canUpdateProducts} />
      )}
      {activeTab === 'informes' && (
        <InformesTab formatCurrency={formatCurrency} canUpdateOrders={canUpdateOrders} updateOrderStatus={updateOrderStatus} />
      )}
      {activeTab === 'site' && <EcommerceSiteEditor />}
      {activeTab === 'envios' && <ShippingMethodsEditor />}
      {activeTab === 'pagos' && <PaymentGatewaysEditor />}
      {activeTab === 'cupones' && <CouponsEditor canUpdate={canUpdateProducts} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal de detalle de pedido — usado desde DashboardTab e InformesTab.
// Carga via /ecommerce/orders/:id y muestra cliente, items, totales, pago
// y direccion de envio.
// ---------------------------------------------------------------------------

type OrderDetail = Omit<Order, 'items'> & {
  items?: {
    sku: string;
    name: string;
    price: number;
    quantity: number;
    image?: string;
    total: number;
  }[];
  totals: {
    subtotal?: number;
    shipping_cost?: number;
    tax_amount?: number;
    discount_amount?: number;
    total: number;
  };
  coupon?: { code: string; discount: number };
  payment: {
    method?: string;
    status: string;
    transaction_id?: string;
    paid_at?: string;
  };
  shipping_address?: {
    first_name?: string;
    last_name?: string;
    street?: string;
    number?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    country?: string;
    phone?: string;
    instructions?: string;
  };
  shipping_method?: { name?: string };
  notes?: string;
  currency?: string;
};

export function OrderDetailModal({
  orderId,
  onClose,
  formatCurrency,
}: {
  orderId: string;
  onClose: () => void;
  formatCurrency: (n: number) => string;
}) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    ecommerceApi
      .getOrder(orderId)
      .then((res) => {
        if (cancelled) return;
        // El backend devuelve { order: {...} } o { data: {...} } segun
        // version. Aceptamos ambos.
        const data = (res.data as { order?: OrderDetail; data?: OrderDetail }).order
          || (res.data as { order?: OrderDetail; data?: OrderDetail }).data
          || (res.data as OrderDetail);
        setOrder(data);
      })
      .catch(() => {
        if (!cancelled) setError('No se pudo cargar el pedido.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const addr = order?.shipping_address;
  const addrFullName = [addr?.first_name, addr?.last_name].filter(Boolean).join(' ');
  const addrLine1 = [addr?.street, addr?.number].filter(Boolean).join(' ');
  const addrLine2 = [addr?.zip_code, addr?.city, addr?.state, addr?.country].filter(Boolean).join(', ');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {order ? `Pedido #${order.order_number}` : 'Pedido'}
            </h2>
            {order && (
              <p className="text-xs text-gray-500 mt-0.5">
                {new Date(order.created_at).toLocaleString()}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading && <p className="text-sm text-gray-500">Cargando…</p>}
          {error && <p className="text-sm text-rose-600">{error}</p>}

          {order && !loading && (
            <>
              {/* Estado + pago */}
              <div className="flex gap-3 flex-wrap">
                <div className="px-3 py-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Estado</p>
                  <div className="mt-1"><StatusBadge status={order.status} /></div>
                </div>
                <div className="px-3 py-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Pago</p>
                  <div className="mt-1">
                    <StatusBadge status={order.payment?.status || 'unknown'} />
                  </div>
                  {order.payment?.method && (
                    <p className="text-xs text-gray-500 mt-1">Método: {order.payment.method}</p>
                  )}
                </div>
              </div>

              {/* Cliente */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Cliente</h3>
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <p className="font-medium text-gray-900">{order.customer?.name}</p>
                  <p className="text-gray-600">{order.customer?.email}</p>
                  {(order.customer as { phone?: string } | undefined)?.phone && (
                    <p className="text-gray-600">{(order.customer as { phone?: string }).phone}</p>
                  )}
                </div>
              </section>

              {/* Items */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Artículos ({order.items?.length || 0})
                </h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">SKU</th>
                        <th className="text-left px-3 py-2">Producto</th>
                        <th className="text-right px-3 py-2">Cant.</th>
                        <th className="text-right px-3 py-2">Precio</th>
                        <th className="text-right px-3 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(order.items || []).map((it, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-gray-500 text-xs">{it.sku}</td>
                          <td className="px-3 py-2 text-gray-900">{it.name}</td>
                          <td className="px-3 py-2 text-right">{it.quantity}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(it.price)}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(it.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Totales */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Totales</h3>
                <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
                  {order.totals?.subtotal !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Subtotal</span>
                      <span>{formatCurrency(order.totals.subtotal)}</span>
                    </div>
                  )}
                  {order.totals?.shipping_cost ? (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Envío</span>
                      <span>{formatCurrency(order.totals.shipping_cost)}</span>
                    </div>
                  ) : null}
                  {order.totals?.tax_amount ? (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Impuestos</span>
                      <span>{formatCurrency(order.totals.tax_amount)}</span>
                    </div>
                  ) : null}
                  {order.totals?.discount_amount ? (
                    <div className="flex justify-between text-emerald-700">
                      <span>Descuento{order.coupon?.code ? ` (cupón ${order.coupon.code})` : ''}</span>
                      <span>-{formatCurrency(order.totals.discount_amount)}</span>
                    </div>
                  ) : null}
                  <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold text-base">
                    <span>Total</span>
                    <span>{formatCurrency(order.totals.total)}</span>
                  </div>
                </div>
              </section>

              {/* Direccion envio */}
              {addr && (addrFullName || addrLine1) && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Envío</h3>
                  <div className="bg-gray-50 rounded-lg p-3 text-sm">
                    {addrFullName && <p className="font-medium text-gray-900">{addrFullName}</p>}
                    {addrLine1 && <p className="text-gray-700">{addrLine1}</p>}
                    {addrLine2 && <p className="text-gray-700">{addrLine2}</p>}
                    {addr.phone && <p className="text-gray-500 mt-1">Tel: {addr.phone}</p>}
                    {order.shipping_method?.name && (
                      <p className="text-gray-500 mt-1">Método: {order.shipping_method.name}</p>
                    )}
                    {addr.instructions && (
                      <p className="text-gray-500 mt-2 italic">{addr.instructions}</p>
                    )}
                  </div>
                </section>
              )}

              {/* Notas */}
              {order.notes && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Notas</h3>
                  <p className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">{order.notes}</p>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
